import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchUserDirectory, updateAccountStatus } from "../api/client";

const PAGE_SIZE = 20;
const DIRECTORY_API = { fetchUserDirectory, updateAccountStatus };

function formatTimestamp(value) {
  if (!value) return "Not recorded";
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? "Timestamp unavailable" : timestamp.toLocaleString();
}

export default function RegisteredAccountDirectory({ api = DIRECTORY_API, actorId }) {
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openActionsId, setOpenActionsId] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionStatus, setActionStatus] = useState("");
  const confirmationRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const nextPage = await api.fetchUserDirectory({ search, limit: PAGE_SIZE, offset });
      setPage(nextPage);
      setError("");
      return nextPage;
    } catch (err) {
      setError(err.message || "Account directory is unavailable.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, offset, search]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (pendingAction) confirmationRef.current?.focus();
  }, [pendingAction]);

  const submitSearch = (event) => {
    event.preventDefault();
    setOffset(0);
    setSearch(searchDraft.trim());
  };
  const pageCount = Math.max(1, Math.ceil(page.total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const confirmStatusChange = async () => {
    if (!pendingAction) return;
    setActionBusy(true);
    setError("");
    setActionStatus("");
    try {
      await api.updateAccountStatus(
        pendingAction.account.id,
        pendingAction.status,
        pendingAction.account.version,
      );
      const refreshed = await refresh();
      const observed = refreshed?.users.find((account) => account.id === pendingAction.account.id);
      if (!observed || observed.status !== pendingAction.status) {
        throw new Error("The update was sent, but the account state could not be confirmed by server readback.");
      }
      setActionStatus(
        `${observed.username} IAM status is ${observed.status.toLowerCase()}; `
        + "provider lifecycle is queued and awaits per-service readback."
      );
      setPendingAction(null);
      setOpenActionsId("");
    } catch (err) {
      if (err.statusCode === 409) {
        await refresh();
        setError("This account changed since the directory was loaded. Current state refreshed; review it before retrying.");
      } else {
        setError(err.message || "Account status could not be changed.");
      }
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="studio-panel iam-pending-panel" aria-labelledby="iam-accounts-title">
      <div className="studio-panel-header">
        <div>
          <div className="studio-eyebrow">REGISTERED IDENTITIES</div>
          <h2 id="iam-accounts-title">Accounts ({page.total})</h2>
        </div>
        <button className="studio-btn-ghost" type="button" onClick={refresh} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <form className="iam-directory-search" onSubmit={submitSearch}>
        <label htmlFor="iam-account-search">Search login or display name</label>
        <div className="iam-directory-search-controls">
          <input id="iam-account-search" type="search" maxLength={120}
            value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} />
          <button className="studio-btn-primary" type="submit">Search</button>
        </div>
      </form>
      {error && (
        <div className="studio-alert studio-alert-attention" role="alert">
          <span>{error}</span>
          <button className="studio-btn-ghost" type="button" onClick={refresh}>Retry</button>
        </div>
      )}
      {loading && page.users.length === 0 && <p className="studio-muted" role="status">Loading accounts…</p>}
      {!loading && !error && page.users.length === 0 && (
        <p className="studio-muted">No registered accounts match this search.</p>
      )}
      {page.users.length > 0 && (
        <>
          {loading && <p className="studio-muted" role="status">Refreshing account results…</p>}
          <div className="iam-directory-table-wrap">
            <table className="iam-directory-table">
              <thead>
                <tr><th scope="col">Identity</th><th scope="col">Roles</th><th scope="col">Account state</th><th scope="col">Created</th><th scope="col">Actions</th></tr>
              </thead>
              <tbody>
                {page.users.map((account) => (
                  <tr key={account.id}>
                    <th scope="row">
                      <strong>{account.display_name}</strong>
                      <span>{account.username}</span>
                      <code>{account.id}</code>
                    </th>
                    <td>{account.roles.length ? account.roles.join(", ") : "No active roles"}</td>
                    <td>{account.status.replaceAll("_", " ")}</td>
                    <td>{formatTimestamp(account.created_at)}</td>
                    <td>
                      {account.id === actorId ? (
                        <span className="studio-muted">Self-management is not available here.</span>
                      ) : account.status === "REVOKED" ? (
                        <span className="studio-muted">Revoked accounts require a new approval.</span>
                      ) : !Number.isInteger(account.version) || account.version < 1 ? (
                        <span className="studio-muted">Status controls unavailable for this record.</span>
                      ) : (
                        <div className="iam-account-actions">
                          <button
                            className="studio-btn-ghost"
                            type="button"
                            aria-expanded={openActionsId === account.id}
                            aria-controls={`iam-account-actions-${account.id}`}
                            onClick={() => {
                              setOpenActionsId((current) => current === account.id ? "" : account.id);
                              setPendingAction(null);
                              setError("");
                              setActionStatus("");
                            }}
                          >
                            Actions
                          </button>
                          {openActionsId === account.id && (
                            <div id={`iam-account-actions-${account.id}`} className="iam-account-action-menu">
                              <button
                                className="studio-btn-ghost"
                                type="button"
                                onClick={() => setPendingAction({
                                  account,
                                  status: account.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE",
                                })}
                              >
                                {account.status === "ACTIVE" ? "Suspend account" : "Restore account"}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className="iam-directory-pagination" aria-label="Account directory pages">
            <span>Page {currentPage} of {pageCount}</span>
            <div>
              <button className="studio-btn-ghost" type="button" disabled={loading || offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
              <button className="studio-btn-ghost" type="button"
                disabled={loading || offset + PAGE_SIZE >= page.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>
          </nav>
        </>
      )}
      {pendingAction && (
        <div className="iam-account-confirmation" role="group" aria-labelledby="iam-account-confirmation-title">
          <h3 id="iam-account-confirmation-title" className="studio-section-kicker">
            Confirm {pendingAction.status === "SUSPENDED" ? "suspension" : "restoration"}
          </h3>
          <p>
            {pendingAction.status === "SUSPENDED"
              ? `Suspend ${pendingAction.account.username}? Their active sessions will be revoked.`
              : `Restore ${pendingAction.account.username} to active status?`}
            {" "}The server rechecks your administrator scope and the account version before saving.
          </p>
          <div className="iam-pending-actions">
            <button
              ref={confirmationRef}
              className="studio-btn-danger"
              type="button"
              onClick={confirmStatusChange}
              disabled={actionBusy}
            >
              {actionBusy ? "Saving…" : `Confirm ${pendingAction.status === "SUSPENDED" ? "suspension" : "restoration"}`}
            </button>
            <button
              className="studio-btn-ghost"
              type="button"
              onClick={() => setPendingAction(null)}
              disabled={actionBusy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {actionStatus && <p className="studio-schedule-success" role="status">{actionStatus}</p>}
    </section>
  );
}
